import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import IcOutlineEdit from '~icons/ic/outline-edit'
import { UIDialog } from 'src/components/ui/Modal'
import { cx } from 'src/utils/cx'
import { getValueFromDispatch } from '../../utils/formUtil'
import useLoadingCallback from '../../utils/useLoadingCallback'
import { UIButton, type UIButtonProps } from '../ui/Button'

export interface UseEditItemsOpt {
  size?: UIButtonProps['size']
  actionButtonName: string
  primaryButtonName?: string
  primaryButtonAfterClick?: () => any
  deleteItem?: (id: string) => Promise<any>
  afterDelete?: () => any
  dialogTitle?: ReactNode
  dialogContent?: ReactNode
  onActionButtonClick?: () => any
  beforeDelete?: (ids: string[]) => any
  renderEditingExtras?: (args: {
    buttonSize: UIButtonProps['size']
    selectedItemIds: string[]
    setSelectedItemIds: (ids: string[]) => void
  }) => ReactNode
}

export type EditItemsState = ReturnType<typeof useEditItems>

export const useEditItems = ({
  size,
  actionButtonName,
  primaryButtonName,
  primaryButtonAfterClick,
  deleteItem,
  afterDelete,
  dialogTitle,
  dialogContent,
  onActionButtonClick,
  beforeDelete,
  renderEditingExtras,
}: UseEditItemsOpt) => {
  const { t } = useTranslation(['artwork', 'action'])
  const [editing, setEditing] = useState(false)
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const deleteItemAction = deleteItem

  const buttonSize: UIButtonProps['size'] = size ?? 'large'

  const deleteLabel = t(
    primaryButtonName
      ? [primaryButtonName, 'artwork:action.delete']
      : 'artwork:action.delete',
  )

  const selectedCountClass = cx(
    'font-medium text-neutral-600 whitespace-nowrap',
    buttonSize === 'small'
      ? 'text-sm'
      : buttonSize === 'xl'
        ? 'text-lg'
        : 'text-base',
  )

  const editButtonPaddingClass = cx(
    'pr-0',
    buttonSize === 'small'
      ? 'sm:pr-2'
      : buttonSize === 'normal'
        ? 'sm:pr-2.5'
        : buttonSize === 'xl'
          ? 'sm:pr-4'
          : 'sm:pr-3',
  )

  const toggleEditing = (editing: boolean) => {
    setEditing(editing)
  }

  const toggleSelected = (id: string) => {
    const newSelectedItems = selectedItems.includes(id)
      ? selectedItems.filter(item => item !== id)
      : [...selectedItems, id]

    setSelectedItems(newSelectedItems)
  }

  const clearSelected = () => {
    setSelectedItems([])
  }

  const cancelSelected = () => {
    setSelectedItems([])
    setEditing(false)
  }

  const [loading, deleteItems] = useLoadingCallback(async () => {
    if (!deleteItemAction) return
    try {
      const selectedItems = await getValueFromDispatch(setSelectedItems)
      beforeDelete?.(selectedItems)

      for (const itemId of selectedItems) {
        await deleteItemAction(itemId)
      }
      afterDelete?.()
      clearSelected()
      toast.success('Delete successfully.')
    } catch (e: any) {
      toast.error('Delete failed.')
    }
  }, [deleteItemAction])

  const editItemsAction = (
    <div className="flex flex-wrap items-center gap-3 justify-end md:flex-nowrap">
      {!editing ? (
        <UIButton
          variant="outlined"
          size={buttonSize}
          className={cx('capitalize', editButtonPaddingClass)}
          labelClassName="hidden sm:inline"
          icon={<IcOutlineEdit className="size-5" />}
          onPress={() => {
            onActionButtonClick?.()
            toggleEditing(true)
          }}
        >
          {actionButtonName}
        </UIButton>
      ) : (
        <>
          <UIButton
            variant="outlined"
            size={buttonSize}
            onPress={cancelSelected}
          >
            {t('action:cancel')}
          </UIButton>

          <span className={selectedCountClass}>
            {selectedItems.length} {'selected'}
          </span>

          {deleteItemAction && (
            <UIButton
              variant="outlined"
              color="danger"
              size={buttonSize}
              disabled={selectedItems.length === 0}
              busy={loading}
              onPress={async () => {
                const toProceed = UIDialog.confirm({
                  heading: dialogTitle,
                  content: dialogContent,
                })
                primaryButtonAfterClick?.()
                if (await toProceed) {
                  await deleteItems()
                }
              }}
            >
              {deleteLabel}
            </UIButton>
          )}

          {renderEditingExtras?.({
            buttonSize,
            selectedItemIds: selectedItems,
            setSelectedItemIds: setSelectedItems,
          })}
        </>
      )}
    </div>
  )

  return {
    editing,
    selectedItems,
    editItemsAction,
    toggleEditing,
    toggleSelected,
    clearSelected,
    cancelSelected,
    deleteItemAction,
    setSelectedItems,
  }
}
